import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useAppState } from '../../../AppStateProvider';
import { AIAgentAnimatedLoader } from '../../../components/AIAgentAnimatedLoader';
import { AnimatedText } from '../../../components/AnimatedText';
import { animateAppearance } from '../../../services/animations';
import { appStore } from '../../../services/appStore';

import { LoadingViewContainer, StyledLoadingText, OnboardingLoadingViewContainer } from './styles';

export type LoadingViewRefObject = {
  disappear(): Promise<void>;
};

type InsightLoadingVariant = 'onboarding' | 'list';

export type LoadingViewProps = { variant: InsightLoadingVariant };

export const LoadingView = forwardRef<LoadingViewRefObject, LoadingViewProps>(({ variant }, ref) => {
  const { t } = useTranslation();
  const { isFirstLoadingAfterOnboarding } = useAppState().emailGenerationOptions;
  const containerRef = useRef<HTMLDivElement>(null);
  const texts = useMemo(() => {
    if (variant === 'onboarding') {
      return [t('insights.onboarding.loading.message_1'), t('insights.onboarding.loading.message_2')];
    }

    if (isFirstLoadingAfterOnboarding && variant === 'list') {
      return [
        t('insights.loading.first_time_message_1'),
        t('insights.loading.first_time_message_2'),
        t('insights.loading.first_time_message_3'),
      ];
    }

    return [t('insights.loading.message_1'), t('insights.loading.message_2')];
  }, [variant, isFirstLoadingAfterOnboarding]);

  useEffect(() => {
    if (!isFirstLoadingAfterOnboarding) {
      return;
    }
    return () => appStore.setIsFirstLoadingAfterOnboarding(false);
  }, []);

  const isListVariant = variant === 'list';
  const isOnboardingVariant = variant === 'onboarding';

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }

    animateAppearance(containerEl, 'appear');
    if (!ref) {
      return;
    }

    const refObject = {
      disappear: () => {
        return animateAppearance(containerEl, 'disappear')?.finished.then(() => {}) ?? Promise.resolve();
      },
    };
    if (typeof ref === 'function') {
      ref(refObject);
    } else {
      ref.current = refObject;
    }
  }, []);

  const Container = isOnboardingVariant ? OnboardingLoadingViewContainer : LoadingViewContainer;

  return (
    <Container ref={containerRef}>
      <AIAgentAnimatedLoader />
      {isListVariant ? (
        <StyledLoadingText data-automation="InsightsLoadingText">{t('insights.loading.title')}</StyledLoadingText>
      ) : null}
      <AnimatedText
        texts={texts}
        speedFactor={isOnboardingVariant ? 0.75 : 1}
        variant={isOnboardingVariant ? 'heading' : 'subtext'}
      />
    </Container>
  );
});
